import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { useState } from 'react';
import { type DeviceInsightEvent, type DeviceVisitEvent, isDeviceVisit } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import hasDataIdentifier from '@/utils/has-data-identifier';
import type { MapLine } from '../../map/map';
import AddressType from '../components/address-row/types';
import useAddressCoordinates from './use-address-coordinates';
import useCountryCoordinates from './use-country-coordinates';

// Every login adds a sign-in, so next to the onboarding's own devices only the latest few show at first.
const COLLAPSED_SIGN_IN_COUNT = 3;
// Collapsing only pays off when it hides a few rows.
const MIN_HIDDEN_SIGN_IN_COUNT = 3;
type Coordinates = {
  lat: number;
  lng: number;
};
type SelectionSource = 'list' | 'map';
type EventEntry = {
  id: string;
  event: DeviceInsightEvent;
  coordinates?: Coordinates;
};
type VisitEntry = {
  id: string;
  visit: DeviceVisitEvent;
  coordinates?: Coordinates;
};
type DeviceEntry = EventEntry | VisitEntry;
type CountryPin = {
  id: string;
  visitId: string;
  country: string;
  coordinates: Coordinates;
};
type AddressEntry = {
  id: string;
  type: AddressType;
  coordinates?: Coordinates;
  isPending: boolean;
};
const toEventEntry = (event: DeviceInsightEvent): EventEntry => {
  const {
    latitude: lat,
    longitude: lng
  } = event.event.data.insight;
  return {
    id: event.id,
    event,
    coordinates: typeof lat === 'number' && typeof lng === 'number' ? {
      lat,
      lng
    } : undefined
  };
};
const toVisitEntry = (visit: DeviceVisitEvent): VisitEntry => {
  const {
    ipLatitude: lat,
    ipLongitude: lng
  } = visit.event.data;
  return {
    id: visit.id,
    visit,
    coordinates: typeof lat === 'number' && typeof lng === 'number' ? {
      lat,
      lng
    } : undefined
  };
};

// A device that says it's in another country than its IP gets a pin on that country too, joined to the IP's.
const getOtherDeviceCountry = ({
  event: {
    data
  }
}: DeviceVisitEvent) => data.deviceCountry && data.deviceCountry !== data.ipCountry ? data.deviceCountry : undefined;
const byNewest = (a: {
  timestamp: string;
}, b: {
  timestamp: string;
}) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
const byNewestEntry = (a: DeviceEntry, b: DeviceEntry) => byNewest('visit' in a ? a.visit : a.event, 'visit' in b ? b.visit : b.event);
const useEntries = (entity, events) => {
  const $ = _c(7);
  const residential = useAddressCoordinates(entity, AddressType.residential);
  const business = useAddressCoordinates(entity, AddressType.business);
  const [selection, setSelection] = useState(null);
  const [pickCount, setPickCount] = useState(0);
  const [isShowingAllSignIns, setIsShowingAllSignIns] = useState(false);
  const insights = events.filter(_temp);
  const deviceEvents = insights.filter(_temp2).map(toEventEntry);
  const visits = events.filter(isDeviceVisit).map(toVisitEntry).sort(_temp3);
  const devices = [...deviceEvents, ...visits].sort(byNewestEntry);
  const signIns = insights.filter(_temp4).map(toEventEntry).sort(_temp5);
  const isSignInsCollapsible = devices.length > 0 && signIns.length - COLLAPSED_SIGN_IN_COUNT >= MIN_HIDDEN_SIGN_IN_COUNT;
  const shownSignIns = isSignInsCollapsible && !isShowingAllSignIns ? signIns.slice(0, COLLAPSED_SIGN_IN_COUNT) : signIns;
  const countryCoordinates = useCountryCoordinates([...new Set(visits.flatMap(_temp6))]);
  let t0;
  if ($[0] !== countryCoordinates) {
    t0 = t1 => {
      const {
        id,
        visit: visit_0
      } = t1;
      const country = getOtherDeviceCountry(visit_0);
      const coordinates = country && countryCoordinates[country];
      return country && coordinates ? [{
        id: `${id}-device`,
        visitId: id,
        country,
        coordinates
      }] : [];
    };
    $[0] = countryCoordinates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const countryPins = visits.flatMap(t0);
  const lines = countryPins.flatMap(t1 => {
    const {
      visitId,
      coordinates: coordinates_0
    } = t1;
    const from = visits.find(t2 => {
      const {
        id: id_0
      } = t2;
      return id_0 === visitId;
    })?.coordinates;
    return from ? [{
      id: visitId,
      from,
      to: coordinates_0
    }] : [];
  });
  const addresses = [];
  const addressSources = [{
    type: AddressType.residential,
    di: "id.address_line1",
    query: residential
  }, {
    type: AddressType.business,
    di: "business.address_line1",
    query: business
  }] as const;
  addressSources.forEach(t3 => {
    const {
      type,
      di,
      query
    } = t3;
    if (!hasDataIdentifier(entity, di)) {
      return;
    }
    const {
      lat,
      lng
    } = query.data ?? {};
    addresses.push({
      id: type,
      type,
      coordinates: lat !== undefined && lng !== undefined ? {
        lat,
        lng
      } : undefined,
      isPending: query.isPending
    });
  });
  const shownEvents = [...deviceEvents, ...shownSignIns].sort(_temp7);
  const entries = [...[...devices, ...shownSignIns].sort(byNewestEntry), ...addresses];
  const pins = [...entries, ...countryPins];
  const defaultId = pins.find(_temp8)?.id ?? null;
  const isSelectionShown = selection !== null && pins.some(t4 => {
    const {
      id: id_1
    } = t4;
    return id_1 === selection.id;
  });
  const selectedId = isSelectionShown ? selection.id : defaultId;
  const selectedCoords = pins.find(t5 => {
    const {
      id: id_2
    } = t5;
    return id_2 === selectedId;
  })?.coordinates;
  const selectedRowId = countryPins.find(t6 => {
    const {
      id: id_3
    } = t6;
    return id_3 === selectedId;
  })?.visitId ?? selectedId;
  let t7;
  if ($[2] !== isShowingAllSignIns || $[3] !== selection?.id || $[4] !== signIns) {
    t7 = () => {
      if (isShowingAllSignIns && signIns.slice(COLLAPSED_SIGN_IN_COUNT).some(t8 => {
        const {
          id: id_4
        } = t8;
        return id_4 === selection?.id;
      })) {
        setSelection(null);
      }
      setIsShowingAllSignIns(!isShowingAllSignIns);
    };
    $[2] = isShowingAllSignIns;
    $[3] = selection?.id;
    $[4] = signIns;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const toggleAllSignIns = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (id_5, source) => {
      setSelection({
        id: id_5,
        source
      });
      setPickCount(_temp9);
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  return {
    devices,
    signIns: shownSignIns,
    signInCount: signIns.length,
    isSignInsCollapsible,
    isShowingAllSignIns,
    toggleAllSignIns,
    shownEvents,
    visits,
    countryPins,
    lines,
    addresses,
    selectedId,
    selectedRowId,
    selectedFrom: isSelectionShown ? selection.source : undefined,
    selectedCoords,
    pickCount,
    select: t8
  };
};
export default useEntries;
function _temp(event) {
  return !isDeviceVisit(event);
}
function _temp2(t0) {
  const {
    event: event_0
  } = t0;
  return event_0.data.kind !== "auth_event";
}
function _temp3(a, b) {
  return byNewest(a.visit, b.visit);
}
function _temp4(t0) {
  const {
    event: event_1
  } = t0;
  return event_1.data.kind === "auth_event";
}
function _temp5(a_0, b_0) {
  return byNewest(a_0.event, b_0.event);
}
function _temp6(t0) {
  const {
    visit
  } = t0;
  return getOtherDeviceCountry(visit) ?? [];
}
function _temp7(a_1, b_1) {
  return byNewest(a_1.event, b_1.event);
}
function _temp8(t0) {
  const {
    coordinates: coordinates_1
  } = t0;
  return coordinates_1;
}
function _temp9(count) {
  return count + 1;
}